import { useMemo } from "react";
import {
  useWidgetRegistry,
  type WidgetConfig,
  type WidgetInstance,
} from "@salestrak/ui";
import type { DatePresetValue } from "#app/lib/utils/date-presets";
import type { SortDirection } from "#app/lib/data";
import { combineWhereConditions } from "#app/lib/query-utils";
import { resolveWidgetFilters } from "./widget-filters";
import { useScopedWhere } from "./scope/use-scoped-where";
import type { WidgetDataSlice } from "./widget-drilldown";
import type { WidgetDimension } from "./use-dimension-counts";
import { at } from "#app/lib/utils/at";

/** Newest-first — the natural order for "what fed this widget". */
export const DRILLDOWN_ORDER_BY: Record<string, SortDirection> = { creationDate: "desc" };

/** The server predicate for a clicked dimension category — the same bucketing
 * the widget's `groupBy` counted, expressed as an issue `where`. Every
 * dimension narrows server-side (`team` can ONLY be server-side —
 * `assigneeTeamIds` expands team → member user ids through membership, which
 * the client cannot resolve), so the drill-down's paged queries read exactly
 * the clicked element's rows. */
function dimensionWhere(
  dimension: WidgetDimension,
  ids: string[],
): Record<string, unknown> | null {
  switch (dimension) {
    case "type":
      return { issueTypeId: { in: ids } };
    case "workflow":
      return { workflowId: { in: ids } };
    case "stage":
      return { stageId: { in: ids } };
    case "team":
      return { assigneeTeamIds: { hasSome: ids } };
    case "assignee":
      return { assignees: { entityId: { hasSome: ids } } };
    case "age":
      return { age: { status: { in: ids } } };
    default:
      return null;
  }
}

/** The server predicate for a clicked chart element — EXACT, so no client
 * re-filter exists downstream. A `dateRange` slice uses `gte`/`lt` bounds,
 * matching the chart bucket's `[startMs, endMs)` semantics precisely;
 * `-Infinity` (a cumulative point) drops the lower bound so the window's own
 * start still applies. */
function sliceWhere(slice: WidgetDataSlice): Record<string, unknown> | null {
  if (slice.kind === "unassigned") {
    return { assignees: { entityId: { isEmpty: true } } };
  }
  const conditions: Record<string, unknown>[] = [];
  if (slice.kind === "dateRange") {
    const bounds: Record<string, string> = {};
    if (Number.isFinite(slice.startMs)) bounds.gte = new Date(slice.startMs).toISOString();
    if (Number.isFinite(slice.endMs)) bounds.lt = new Date(slice.endMs).toISOString();
    if (Object.keys(bounds).length > 0) conditions.push({ [slice.dateField]: bounds });
  }
  if (slice.dimension && slice.ids?.length) {
    const dim = dimensionWhere(slice.dimension, slice.ids);
    if (dim) conditions.push(dim);
  }
  if (conditions.length === 0) return null;
  return conditions.length === 1 ? at(conditions, 0, "condition") : { AND: conditions };
}

/**
 * Data layer for the widget detail dialog's Data drill-down: the ONE folded
 * `where` covering the issue rows feeding a widget, for the panel's windowed
 * paged queries — the panel reaches EVERY row in the widget's window, never a
 * capped chunk. Reproduces the widget's own data window off its resolved
 * config (defaults applied): the widget's `filters` blob folds first
 * (`resolveWidgetFilters` — archived-out unless shown, saved query, widget date
 * range), the date column follows `measure` ("updated" widgets filter on
 * lastUpdateDate), and a top-level `dateRange` key on the config still overrides
 * the ambient window when the blob doesn't — everything else inherits the
 * dashboard scope, exactly as the widget's own data hook folds it. A clicked
 * chart element (`slice`) AND-folds its exact server predicate. So the rows
 * ARE the widget's inputs.
 */
export function useWidgetDrilldownWhere(
  instance: WidgetInstance,
  /** The active chart-element filter, if any — folded as an exact server predicate. */
  slice?: WidgetDataSlice | null,
): {
  where: Record<string, unknown>;
  /** Serialized `where` — flips on scope/filter/slice changes. */
  variablesKey: string;
} {
  const registry = useWidgetRegistry();
  const def = registry.get(instance.type);

  const resolved = useMemo(
    () => registry.resolveConfig(def, instance.config as WidgetConfig, instance.schemaVersion ?? 1),
    [registry, def, instance.config, instance.schemaVersion],
  );
  // The date column mirrors each widget's own reader: an explicit `dateField`
  // wins; `measure: "updated"` widgets bucket on last update; the managed
  // team-overview activity/workload tiles (task #8 catalogs) do too.
  const dateField =
    resolved.dateField === "lastUpdateDate" ||
    resolved.measure === "updated" ||
    resolved.widgetKey === "team-overview.active" ||
    resolved.widgetKey === "team-overview.workload"
      ? ("lastUpdateDate" as const)
      : ("creationDate" as const);
  const { filters, base } = resolveWidgetFilters(resolved.filters);
  const dateRange = (filters.dateRange ??
    resolved.dateRange ??
    undefined) as DatePresetValue | undefined;
  const { where: scopedWhere } = useScopedWhere(base, {
    dateField,
    override: dateRange ? { dateRange } : undefined,
  });

  // The key serializes exactly the fields the slice predicate reads, so the
  // folded `where` stays referentially stable across renders (a slice object
  // is a fresh ref per click, never per render — but the guard costs nothing
  // and documents the contract).
  const sliceKey = slice
    ? JSON.stringify([
        slice.kind,
        slice.kind !== "unassigned" ? (slice.dimension ?? null) : null,
        slice.kind !== "unassigned" ? (slice.ids ?? null) : null,
        slice.kind === "dateRange" ? [slice.dateField, slice.startMs, slice.endMs] : null,
      ])
    : "";
  const where = useMemo(() => {
    const narrowed = slice ? sliceWhere(slice) : null;
    return narrowed
      ? (combineWhereConditions([scopedWhere, narrowed]) ?? scopedWhere)
      : scopedWhere;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedWhere, sliceKey]);
  const variablesKey = useMemo(() => JSON.stringify(where), [where]);

  return { where, variablesKey };
}
